import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hipobuyspread.pics';
const logoUrl = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/16c9e81c106f6a445869e4a45760203f.jpeg';

const articleJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Article",
      "@id": `${siteUrl}/hipobuy-spreadsheet-guide#article`,
      "headline": "The Complete Hipobuy Spreadsheet Guide 2026: Everything You Need To Know",
      "description": "Comprehensive guide covering everything about how Hipobuy Spreadsheet works, its benefits, category deep-dives, safety tips, comparisons, and FAQs for smarter product discovery.",
      "image": "https://readdy.ai/api/search-image?query=Modern%20digital%20catalog%20displayed%20on%20large%20tablet%20screen%20surrounded%20by%20fashion%20products%20on%20white%20desk%2C%20organized%20spreadsheet%20interface%20visible%2C%20bright%20natural%20lighting%20from%20large%20window%2C%20professional%20product%20research%20workspace%2C%20clean%20editorial%20photography%2C%20warm%20neutral%20tones%20with%20soft%20shadows&width=1200&height=600&seq=article-hero-spreadsheet-guide-v2&orientation=landscape",
      "author": {
        "@type": "Organization",
        "name": "Hipobuy Spread Editorial Team",
        "url": siteUrl,
      },
      "publisher": {
        "@type": "Organization",
        "name": "Hipobuy Spreadsheet",
        "logo": {
          "@type": "ImageObject",
          "url": logoUrl,
        },
      },
      "datePublished": "2026-06-15",
      "dateModified": "2026-07-09",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${siteUrl}/hipobuy-spreadsheet-guide`,
      },
      "inLanguage": "en",
      "wordCount": 2600,
      "timeRequired": "PT12M",
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": siteUrl,
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Hipobuy Spreadsheet Guide",
          "item": `${siteUrl}/hipobuy-spreadsheet-guide`,
        },
      ],
    },
  ],
};

export default function HipobuySpreadsheetGuide() {
  return (
    <>
      <JsonLd data={articleJsonLd} />
      <Navbar />
      <main className="pt-20">
        <article>
          {/* Article Header */}
          <header className="w-full py-12 md:py-16 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <div className="flex items-center gap-2 mb-4">
                  <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer flex items-center gap-1">
                    <i className="ri-arrow-left-line text-xs"></i>
                    <span>Back to Home</span>
                  </Link>
                </div>
                <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 mb-4">
                  Spreadsheet Guide
                </span>
                <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                  The Complete Hipobuy Spreadsheet Guide 2026: Everything You Need To Know
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span>Updated: July 9, 2026</span>
                  <span>12 min read</span>
                  <span>By Hipobuy Spread Editorial Team</span>
                </div>
              </div>
            </div>
          </header>

          {/* Featured Image */}
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6 mb-12">
            <div className="rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20digital%20catalog%20displayed%20on%20large%20tablet%20screen%20surrounded%20by%20fashion%20products%20on%20white%20desk%2C%20organized%20spreadsheet%20interface%20visible%2C%20bright%20natural%20lighting%20from%20large%20window%2C%20professional%20product%20research%20workspace%2C%20clean%20editorial%20photography%2C%20warm%20neutral%20tones%20with%20soft%20shadows&width=1200&height=600&seq=article-hero-spreadsheet-guide-v2&orientation=landscape"
                alt="Hipobuy Spreadsheet complete guide featured image"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          {/* Article Content */}
          <div className="w-full px-4 md:px-6 pb-16 md:pb-20">
            <div className="max-w-3xl mx-auto">
              <div className="prose-custom space-y-8">
                {/* Introduction */}
                <section>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    Introduction: Why Spreadsheet Shopping Is Transforming Product Discovery
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    In an era where online shopping can feel overwhelming — with thousands of products scattered across dozens of pages — the <strong>Hipobuy Spreadsheet</strong> has emerged as a game-changing resource for smart shoppers. Rather than clicking through endless product listings, users can now browse organized, structured collections that put all the essential information in one place.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    This comprehensive guide covers everything you need to know about using spreadsheet resources for product discovery. Whether you are a complete beginner or an experienced shopper looking to optimize your research process, you will find actionable insights, safety tips, and comparison strategies throughout this article.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    By the end of this guide, you will understand how the Hipobuy Spreadsheet works, why thousands of shoppers rely on it daily, and how you can leverage it to make better, faster, and more informed purchasing decisions across every product category from shoes to accessories.
                  </p>
                </section>

                {/* Section 1: What is Hipobuy Spreadsheet */}
                <section>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    What Exactly Is a Hipobuy Spreadsheet?
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    At its core, a <strong>Hipobuy Spreadsheet</strong> is a meticulously organized digital catalog that compiles product information into a structured, easy-to-navigate format. Think of it as a centralized dashboard where all relevant product details — names, prices, sizing options, material specifications, and availability — are displayed in a clear, column-based layout.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    Unlike traditional online stores where you might spend hours scrolling through individual product pages, the spreadsheet format allows you to scan dozens or even hundreds of items in minutes. Each row typically represents a distinct product, while columns categorize essential attributes such as category, size range, color options, and price points.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    The Hipobuy Spreadsheet specifically stands out because of its comprehensive category coverage. From footwear and apparel to accessories and fragrances, the spreadsheet organizes products across 14 distinct categories, making it one of the most versatile product discovery tools available to online shoppers today.
                  </p>
                  <div className="bg-background-100 rounded-2xl p-5 md:p-6 my-6 border border-background-200/70">
                    <h3 className="font-heading text-base font-bold text-foreground-900 mb-3">Key Features of Hipobuy Spreadsheet:</h3>
                    <ul className="space-y-2">
                      {[
                        "14 product categories covering shoes, apparel, accessories, and more",
                        "Regularly updated listings to reflect current inventory and trends",
                        "Side-by-side product comparisons for informed decision making",
                        "Detailed item descriptions including material, sizing, and pricing information",
                        "Intuitive navigation that works for both beginners and experienced shoppers",
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2.5 text-sm text-foreground-700">
                          <i className="ri-check-line text-secondary-600 mt-0.5 flex-shrink-0"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                {/* Section 2: How It Works */}
                <section>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    How Does the Hipobuy Spreadsheet Work?
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    Understanding how to effectively use the Hipobuy Spreadsheet is the first step toward transforming your shopping experience. The process is designed to be intuitive, but knowing the workflow will help you maximize efficiency from day one.
                  </p>

                  <h3 className="font-heading text-lg font-bold text-foreground-900 mt-6 mb-3">
                    Step 1: Choose Your Category
                  </h3>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    Start by selecting the product category that interests you. The spreadsheet organizes items into clearly defined sections such as Shoes, Hoodies, T-Shirts, Jackets, Bags, and more. This categorical approach eliminates the noise of unrelated products and helps you focus your research.
                  </p>

                  <h3 className="font-heading text-lg font-bold text-foreground-900 mt-6 mb-3">
                    Step 2: Scan the Organized Listings
                  </h3>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    Once inside a category, you will see products displayed in a structured format. Each entry includes essential details such as the product name, available sizes, color variants, and pricing. The organized layout makes it easy to scan multiple items quickly and identify the ones that match your preferences.
                  </p>

                  <h3 className="font-heading text-lg font-bold text-foreground-900 mt-6 mb-3">
                    Step 3: Compare Products Side by Side
                  </h3>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    One of the greatest advantages of the spreadsheet format is the ability to compare products directly. Instead of opening multiple browser tabs and switching between them, you can view critical information for several items simultaneously, making comparison shopping significantly more efficient.
                  </p>

                  <h3 className="font-heading text-lg font-bold text-foreground-900 mt-6 mb-3">
                    Step 4: Make an Informed Decision
                  </h3>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    With all the relevant information at your fingertips, you can confidently select the products that best match your needs. The spreadsheet approach removes the guesswork and helps you avoid impulse purchases driven by limited information.
                  </p>
                </section>

                {/* Section 3: Benefits */}
                <section>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    Top Benefits of Using Hipobuy Spreadsheet for Product Discovery
                  </h2>

                  <div className="space-y-5">
                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-timer-flash-line text-accent-600"></i>
                      </div>
                      <div>
                        <h3 className="font-heading text-base font-bold text-foreground-900 mb-1">Massive Time Savings</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          Traditional online shopping can take hours of scrolling through individual product pages. The Hipobuy Spreadsheet condenses this process into minutes by displaying all relevant information in a single, organized view. Shoppers report saving anywhere from 50% to 80% of their typical browsing time.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-stack-line text-secondary-600"></i>
                      </div>
                      <div>
                        <h3 className="font-heading text-base font-bold text-foreground-900 mb-1">Comprehensive Category Coverage</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          With 14 distinct product categories, the spreadsheet covers virtually every shopping need. Whether you are looking for the latest sneaker releases, trendy hoodies, or premium accessories, everything is organized under one accessible resource.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-refresh-line text-primary-600"></i>
                      </div>
                      <div>
                        <h3 className="font-heading text-base font-bold text-foreground-900 mb-1">Always Up to Date</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          The Hipobuy Spreadsheet is regularly refreshed to reflect current inventory levels, new product additions, and trending items. This ensures that you are always working with accurate, relevant information rather than outdated listings that waste your time.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-emotion-happy-line text-accent-600"></i>
                      </div>
                      <div>
                        <h3 className="font-heading text-base font-bold text-foreground-900 mb-1">Beginner-Friendly Experience</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          You do not need to be a tech expert or experienced shopper to benefit from spreadsheet resources. The intuitive layout and clear category organization make it accessible to anyone who wants to shop smarter, regardless of their experience level.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Section 4: Category Deep Dive */}
                <section>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    Popular Product Categories in the Hipobuy Spreadsheet
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    The Hipobuy Spreadsheet organizes products into carefully defined categories that cover the full spectrum of consumer shopping interests. Here is a closer look at some of the most popular categories and what you can expect to find in each.
                  </p>

                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="border-b border-background-200">
                          <th className="text-left py-3 px-4 font-semibold text-foreground-900">Category</th>
                          <th className="text-left py-3 px-4 font-semibold text-foreground-900">What You Will Find</th>
                          <th className="text-left py-3 px-4 font-semibold text-foreground-900">Best For</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { cat: "Shoes", find: "Sneakers, casual shoes, athletic footwear, and seasonal styles", best: "Sneaker enthusiasts and casual shoppers" },
                          { cat: "Hoodies / Sweaters", find: "Pullover hoodies, zip-ups, crewneck sweaters, and knitwear", best: "Streetwear fans and comfort seekers" },
                          { cat: "T-Shirts", find: "Graphic tees, plain basics, branded designs, and seasonal prints", best: "Everyday wardrobe builders" },
                          { cat: "Jackets", find: "Bombers, denim jackets, puffer coats, and lightweight layers", best: "Outerwear collectors" },
                          { cat: "Accessories", find: "Watches, sunglasses, jewelry, phone cases, and small leather goods", best: "Detail-oriented shoppers" },
                          { cat: "Bags", find: "Backpacks, messenger bags, totes, and travel luggage", best: "Students and travelers" },
                        ].map((row, i) => (
                          <tr key={row.cat} className={`border-b border-background-100 ${i % 2 === 0 ? "bg-background-50" : "bg-transparent"}`}>
                            <td className="py-3 px-4 font-medium text-foreground-800">{row.cat}</td>
                            <td className="py-3 px-4 text-foreground-600">{row.find}</td>
                            <td className="py-3 px-4 text-foreground-600">{row.best}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Section 5: Safety */}
                <section>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    Is the Hipobuy Spreadsheet Safe? A Complete Security Guide
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    Safety is a top concern for anyone using online shopping resources, and it is important to address this directly. The <strong>Hipobuy Spreadsheet</strong> itself serves as an informational resource — a catalog of product listings — rather than a transaction platform. This means you are browsing organized product information, not entering payment details or personal data through the spreadsheet itself.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    That said, there are important safety practices every shopper should follow when using any spreadsheet-based shopping resource. Here are the essential guidelines to keep your browsing experience secure.
                  </p>

                  <div className="bg-secondary-50/50 rounded-2xl p-5 md:p-6 my-6 border border-secondary-200/50">
                    <h3 className="font-heading text-base font-bold text-foreground-900 mb-3 flex items-center gap-2">
                      <i className="ri-shield-check-line text-secondary-600"></i>
                      Safety Best Practices
                    </h3>
                    <ul className="space-y-3">
                      {[
                        "Always verify that you are accessing spreadsheet resources from trusted, community-verified sources",
                        "Never share personal information such as passwords, payment details, or identification through spreadsheet platforms",
                        "Cross-reference product details with official store listings before making purchasing decisions",
                        "Use secure, encrypted connections (HTTPS) when browsing any shopping-related websites",
                        "Be cautious of any spreadsheet that requests downloads or asks you to enable macros or scripts",
                        "Check for regular update timestamps — frequently updated spreadsheets are generally more reliable",
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2.5 text-sm text-foreground-700">
                          <i className="ri-check-double-line text-secondary-500 mt-0.5 flex-shrink-0"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                {/* Section 6: Comparison */}
                <section>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    Hipobuy Spreadsheet vs Other Shopping Methods
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    To truly appreciate the value of spreadsheet-based product discovery, it helps to compare it with traditional shopping approaches. The table below highlights the key differences across several important dimensions.
                  </p>

                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="border-b-2 border-background-200">
                          <th className="text-left py-3 px-4 font-semibold text-foreground-900">Aspect</th>
                          <th className="text-center py-3 px-4 font-semibold text-foreground-600">Traditional Browsing</th>
                          <th className="text-center py-3 px-4 font-semibold text-primary-600 bg-primary-50/50">Hipobuy Spreadsheet</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { aspect: "Browsing Speed", trad: "5-10 seconds per product", spread: "Scan 50+ products per minute" },
                          { aspect: "Product Comparison", trad: "Multiple tabs required", spread: "Side-by-side in one view" },
                          { aspect: "Category Navigation", trad: "Click through menu layers", spread: "Direct category access" },
                          { aspect: "Information Density", trad: "One product per page", spread: "Multiple products per view" },
                          { aspect: "Update Frequency", trad: "Varies by store", spread: "Regular refreshes" },
                        ].map((row, i) => (
                          <tr key={row.aspect} className={`border-b border-background-100 ${i % 2 === 0 ? "bg-background-50" : "bg-transparent"}`}>
                            <td className="py-3 px-4 font-medium text-foreground-800">{row.aspect}</td>
                            <td className="py-3 px-4 text-center text-foreground-500">{row.trad}</td>
                            <td className="py-3 px-4 text-center font-medium text-primary-600 bg-primary-50/30">{row.spread}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Section 7: FAQ */}
                <section>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    Frequently Asked Questions About Hipobuy Spreadsheet
                  </h2>

                  <div className="space-y-4">
                    {[
                      {
                        q: "What is a Hipobuy Spreadsheet?",
                        a: "A Hipobuy Spreadsheet is an organized digital resource that compiles product listings across multiple categories into a structured, easy-to-navigate format. It helps shoppers discover and compare products efficiently without scrolling through individual product pages.",
                      },
                      {
                        q: "How often is the Hipobuy Spreadsheet updated?",
                        a: "The Hipobuy Spreadsheet is updated regularly to reflect current inventory, new product additions, and trending items. This ensures shoppers always have access to accurate and relevant product information.",
                      },
                      {
                        q: "Is it free to use the Hipobuy Spreadsheet?",
                        a: "Yes, the Hipobuy Spreadsheet is a free informational resource designed to help shoppers discover and compare products across multiple categories. There is no cost to browse the organized product listings.",
                      },
                      {
                        q: "Do I need to create an account to use the spreadsheet?",
                        a: "No account creation is required. You can browse the organized product categories and listings without signing up or providing any personal information.",
                      },
                      {
                        q: "What product categories are available?",
                        a: "The spreadsheet covers 14 categories including Shoes, Hoodies, T-Shirts, Jackets, Pants, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff.",
                      },
                      {
                        q: "How do I compare products using the spreadsheet?",
                        a: "The spreadsheet format allows you to view multiple products simultaneously in a structured layout. You can scan product names, prices, sizes, and other details side by side to make informed comparisons.",
                      },
                    ].map((faq) => (
                      <div key={faq.q} className="rounded-2xl bg-background-100 border border-background-200/70 p-5">
                        <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">{faq.q}</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 8: Conclusion */}
                <section>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    Conclusion: Start Your Smarter Shopping Journey Today
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    The <strong>Hipobuy Spreadsheet</strong> represents a significant evolution in how people discover and compare products online. By organizing comprehensive product information into a structured, accessible format, it eliminates the friction and inefficiency that characterize traditional online shopping experiences.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    Whether you are searching for the latest sneaker releases, building a seasonal wardrobe, or simply exploring new product categories, the spreadsheet approach offers a faster, more organized, and more informed way to shop. The combination of comprehensive category coverage, regular updates, and intuitive navigation makes it an invaluable resource for both casual browsers and dedicated shoppers.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    Ready to experience the difference? Explore the Hipobuy Spreadsheet categories today and discover a smarter approach to product discovery. Your next great find is just a few organized rows away.
                  </p>
                </section>

                {/* CTA */}
                <div className="rounded-2xl bg-primary-500 p-6 md:p-8 text-center">
                  <h3 className="font-heading text-xl font-bold text-background-50 mb-3">
                    Ready to Explore the Hipobuy Spreadsheet?
                  </h3>
                  <p className="text-sm text-background-50/80 mb-5 max-w-md mx-auto">
                    Browse organized product categories and discover a better way to shop online.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="px-6 py-3 rounded-full text-sm font-semibold text-primary-600 bg-background-50 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Explore Hipobuy Sheet
                    </a>
                    <Link
                      to="/"
                      className="px-6 py-3 rounded-full text-sm font-semibold text-background-50 bg-background-50/20 hover:bg-background-50/30 border border-background-50/30 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Back to Homepage
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}